// src/components/support/forms/RaiseTicket.js
import React, { useState } from "react";
import { ChevronLeft } from "lucide-react";
import { useRequestProcessor } from "../../../requestProcessor";
import SpinnerLoading from "../../common/SpinnerLoading";
import { submitIssue } from "../../../queryfunctions/queryfunctions";
import { useAtom } from "jotai";
import { childList, appConstants, activeChildDetail } from "../../../atomstore/store";
import { useNavigate } from "react-router-dom";
import CustomSelect from "../../common/CustomSelect";

const RaiseTicket = () => {
    const navigate = useNavigate();
    const { useMutationWrapper } = useRequestProcessor();

    const [children] = useAtom(childList);
    const [activeChild, setActiveChild] = useAtom(activeChildDetail);
    const [constants] = useAtom(appConstants);

    const [issue, setIssue] = useState("");
    const [description, setDescription] = useState("");
    const [otherRemark, setOtherRemark] = useState("");
    const [error, setError] = useState("");

    const raiseIssue = useMutationWrapper(
        ["raiseIssue"],
        () => {
            const finalDescription =
                issue === "Others" ? `${otherRemark} - ${description}` : description;

            return submitIssue(issue, finalDescription);
        },
        {
            onSuccess: (data) => {
                console.log("[Raise Ticket] Submitted:", data);
            },
        }
    );

    const validate = () => {
        if (!issue) {
            setError("Please select an issue");
            return false;
        }
        if (issue === "Others" && !otherRemark.trim()) {
            setError("Please enter other issue");
            return false;
        }
        if (!description.trim()) {
            setError("Please enter description");
            return false;
        }
        setError("");
        return true;
    };

    const submitHandler = () => {
        if (validate()) raiseIssue.mutate();
    };

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {raiseIssue.isLoading && <SpinnerLoading />}

            {/* HEADER */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>Raise a Ticket</h2>
            </div>

            {/* CONTENT */}
            <div className="p-4 space-y-4">

                {/* Select Child - matches TakeBreak */}
                {children.length > 1 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Child
                        </label>
                        <CustomSelect
                            value={activeChild?.id}
                            onChange={(val) =>
                                setActiveChild(
                                    children.find((c) => c.id.toString() === val.toString())
                                )
                            }
                            options={children.map((child) => ({
                                value: child.id,
                                label: `${child.name} ${child.last_name}`,
                            }))}
                            placeholder="Select Child"
                        />
                    </div>
                )}

                {/* Info Note */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                        Our team will contact you within one business day.
                        <br />
                        For urgent matters, please reach out to your Centre Head.
                    </p>
                </div>

                {/* Issue List */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Issue
                    </label>

                    <div className="space-y-2">
                        {constants?.issueList?.map((item, index) => (
                            <label
                                key={index}
                                className="flex items-center gap-3 px-4 py-3 border border-gray-300 rounded-xl cursor-pointer"
                            >
                                <input
                                    type="radio"
                                    name="issue"
                                    value={item}
                                    checked={issue === item}
                                    onChange={() => {
                                        setIssue(item);
                                        setError("");
                                    }}
                                    className="h-4 w-4"
                                />
                                <span className="text-sm">{item}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Other Issue Input */}
                {issue === "Others" && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Other Issue
                        </label>
                        <input
                            type="text"
                            placeholder="Enter other issue"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500"
                            onChange={(e) => {
                                setOtherRemark(e.target.value);
                                setError("");
                            }}
                        />
                    </div>
                )}

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                    </label>
                    <textarea
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 h-32 outline-none focus:border-transparent"
                        placeholder="Write your issue here"
                        onChange={(e) => {
                            setDescription(e.target.value);
                            setError("");
                        }}
                    ></textarea>
                </div>

                {/* Error */}
                {error && <p className="text-red-500 text-sm">{error}</p>}

                {/* Submit Button */}
                <button
                    onClick={submitHandler}
                    disabled={raiseIssue.isLoading}
                    className="w-full bg-cyan-500 text-white py-3 rounded-xl font-semibold hover:bg-cyan-600 transition disabled:opacity-60"
                >
                    Submit Ticket
                </button>
            </div>
        </div>
    );
};

export default RaiseTicket;
